import React, { useState, useEffect, useCallback } from "react";
import { useSearchParams } from "react-router-dom";

import { useGetExactDocumentsMutation } from "../../../store/api/lumi/documents";
import { useGetLLMPassThroughMutation } from "../../../store/api/lumi/llm";
import SummaryHeader from "../../Summary/SummaryHeader";
import SummaryDocuments from "../../Summary/SummaryDocuments";
import SummaryText from "../../Summary/SummaryText";
import SummaryContainer from "../../Summary/SummaryContainer";
import { Collapse, Pagination, Tooltip } from "antd";
import { CheckOutlined, CopyOutlined } from "@ant-design/icons";
import LoadingIndicator from "../../Summary/LoadingIndicator";
import Scrollbars from "react-custom-scrollbars-2";
import {Typography } from 'antd';

const { Text} = Typography;
const CopyToClipboard = ({ content }) => {
    const [copied, setCopied] = useState(false);
    const handleCopy = () => {
        navigator.clipboard.writeText(content);
        setCopied(true);
        // message.success("Copied to clipboard!");
        setTimeout(() => setCopied(false), 2000);
      };

    return (
        <Tooltip title={copied ? "Copied!" : "Copy"}>
          {copied ? <CheckOutlined style={{ color: "green" }} /> : <CopyOutlined onClick={handleCopy} style={{ cursor: "pointer" }} />}
        </Tooltip>
    );

}

const NewSummaryText = ({ content, loading }) => {
    if (loading) {
        return <LoadingIndicator />
    }
    return (
        <p style={{ maxHeight:"150px", overflow:"auto", color:"#353935", lineHeight:"19px", margin:"0", padding:"0 5px"}}>{content || ""}</p>
    )
}

const NewSummaryDocuments = ({ loading, documents }) => {
    const [documentsIndex, setDocumentsIndex] = useState(1);
    if (loading) {
        return <LoadingIndicator />;
    }

    return (
        <div onMouseDown={(e) => e.stopPropagation()}>
            <Scrollbars style={{ height: "180px" }}>
                {documents && documents.length > 0 ? (
                    <p style={{ maxHeight:"150px", overflow:"auto", color:"#353935", lineHeight:"19px", margin:"0", padding:"0 5px" }}>
                        {documents[documentsIndex - 1].text}
                    </p>
                ) : (
                    <p>no documents found</p>
                )}
            </Scrollbars>
            <div style={{textAlign:"center"}}>
            <Pagination
                simple={{
                    readOnly: true,
                }}
                defaultCurrent={2}
                current={documentsIndex}
                total={documents.length}
                onChange={(page) => setDocumentsIndex(page)}
                showSizeChanger={false}
                pageSize={1}
            />
            </div>
        </div>
    );


}

const VolumeSummary = ({ id, selectedConcept }) => {
    const [triggerGetDocuments] = useGetExactDocumentsMutation();
    const [triggerGetLLMPassThrough] = useGetLLMPassThroughMutation();

    const [conceptDocuments, setConceptDocuments] = useState([]);
    const [loadingConceptDocuments, setLoadingConceptDocuments] = useState([]);

    const [conceptSummary, setConceptSummary] = useState(null);
    const [loadingConceptSummary, setLoadingConceptSummary] = useState(false);
    const [searchParams] = useSearchParams();
    const isNewApp = searchParams.get("app") === "new";

    const fetchDocuments = useCallback(async () => {
        setLoadingConceptDocuments(true);
        try {
            const result = await triggerGetDocuments({
                id,
                filter: [],
                search: selectedConcept.texts,
            }).unwrap();

            setConceptDocuments(result);
        } catch (error) {
            console.error("Failed to fetch documents:", error);

            setConceptDocuments([]);
        } finally {
            setLoadingConceptDocuments(false);
        }
    }, [triggerGetDocuments, id, selectedConcept]);

    useEffect(() => {
        if (selectedConcept)
        fetchDocuments();
    }, [selectedConcept]);

    const handleLlmRequest = useCallback(
        async (prompt) => {
            try {
                const llmResponse = await triggerGetLLMPassThrough({
                    projectId: id,
                    prompt: prompt,
                }).unwrap();

                return llmResponse.response;
            } catch (error) {
                console.error("failed to fetch summary", error);
                return null;
            }
        },
        [id, prompt]
    );

    const fetchSummary = useCallback(async () => {
        if (!selectedConcept) return;

        setLoadingConceptSummary(true);
        try {
            const prompt =
                `Summarize how "${selectedConcept.texts.join(", ")}" is spoken about in these documents\n` +
                conceptDocuments.map((d) => `<p>${d.text}</p>`).join("");

            const summary = await handleLlmRequest(prompt);
            setConceptSummary(summary);
        } catch (error) {
            console.error("Failed to fetch summaries:", error);
        } finally {
            setLoadingConceptSummary(false);
        }
    }, [conceptDocuments, handleLlmRequest]);

    useEffect(() => {
        fetchSummary();
    }, [conceptDocuments]);

    if (!selectedConcept)
        return (
            <SummaryContainer>
                <SummaryHeader>
                    Select a concept to show detailed summary.
                </SummaryHeader>
            </SummaryContainer>
        );
        if (isNewApp) {
            const items = [
                {
                    key: "1",
                    label: <Text strong style={{fontSize: "16px", color:"#090A09", fontWeight:"700"}}>{`Summary for ${selectedConcept.concept}`}</Text>,
                    children: (
                        <NewSummaryText
                        header={`Summary for ${selectedConcept.concept}`}
                        loading={loadingConceptSummary}
                        summary={conceptSummary}
                        />
                    ),
                    extra: <CopyToClipboard content={conceptSummary} />,
                },
                {
                    key: "2",
                    label: <Text strong style={{fontSize: "16px", color:"#090A09", fontWeight:"700"}}>{`Documents for ${selectedConcept.concept}`}</Text>,
                    children: (
                        <NewSummaryDocuments
                        header={`Documents for ${selectedConcept.concept}`}
                        loading={loadingConceptDocuments}
                        documents={conceptDocuments}
                        />
                    ),
                },
            ];
    
            return <Collapse accordion ghost items={items} defaultActiveKey={["2"]}/>;
        }     

    return (
        <>
            <SummaryText
                header={`Summary for ${selectedConcept.concept}`}
                loading={loadingConceptSummary}
                summary={conceptSummary}
            />
            <SummaryDocuments
                header={`Documents for ${selectedConcept.concept}`}
                loading={loadingConceptDocuments}
                documents={conceptDocuments}
            />
        </>
    );
};

export default VolumeSummary;
